package ui.device

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import common.frontend.api.Io
import compose.assets.Svg
import compose.assets.svg.Cellphone_arrow_down
import compose.assets.svg.Check
import compose.assets.svg.Close
import provider.LocalAccountProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.EFieldList
import tools.EFieldTextString
import ui.widget.ListingItemBody
import ui.widget.ListingSpecs
import kotlin.time.Duration.Companion.seconds


interface DeviceListingScope {

    val vm: DeviceListingVM
    val action: (DeviceListingVM.Action) -> Unit

    val nav: Navigation
    val login: Io.User
    val args: DeviceListingArgs
    val headerSpecs: ListingSpecs<Wrapper.Item>

    companion object {

        private val itemStatus by lazy {
            ListingSpecs.Item<Wrapper.Item>(title = Res.string.device_listing__item_connection, width = 200.dp) {
                ListingItemBody.Composable { modifier ->
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        Icon(
                            imageVector = if (it.data.connectivity.online) Svg.Check else Svg.Close,
                            contentDescription = null,
                            tint = if (it.data.connectivity.online) MaterialTheme.colorScheme.onSecondary else MaterialTheme.colorScheme.onError,
                            modifier = modifier.size(24.dp)
                                .background(
                                    if (it.data.connectivity.online) MaterialTheme.colorScheme.secondary else MaterialTheme.colorScheme.error,
                                    CircleShape
                                ).padding(4.dp)
                        )
                        Text(it.data.connectivity.change.inWholeSeconds.seconds.toString())
                    }

                }
            }
        }

        private val itemLayerTop by lazy {
            ListingSpecs.Item<Wrapper.Item>(
                title = Res.string.device_listing__item_layer_top,
                align = TextAlign.Center,
                width = 100.dp
            ) {
                if (it.data.layer.top) {
                    it.data.layer.topCount.toString()
                } else {
                    ListingItemBody.Icon(Svg.Close)
                }
            }
        }

        private val itemLayerBottom by lazy {
            ListingSpecs.Item<Wrapper.Item>(
                title = Res.string.device_listing__item_layer_bottom,
                align = TextAlign.Center,
                width = 100.dp
            ) {
                if (it.data.layer.bottom) {
                    it.data.layer.bottomCount.toString()
                } else {
                    ListingItemBody.Icon(Svg.Close)
                }
            }
        }

        private val itemUpdateState by lazy {
            ListingSpecs.Item<Wrapper.Item>(
                title = Res.string.device_listing__item_updated,
                align = TextAlign.Center,
                width = 100.dp
            ) { item ->
                ListingItemBody.Icon(
                    if (item.data.apps.any { it.lastStable > it.version }) Svg.Cellphone_arrow_down
                    else Svg.Check
                )
            }
        }

        private val todayImpressions by lazy {
            ListingSpecs.Item<Wrapper.Item>(
                title = Res.string.device_listing__item_impressions_of_today,
                align = TextAlign.Center,
                width = 100.dp
            ) { item ->
                if (item.data.layer.top) {
                    item.data.campaignInfo.countImpressionsToday
                } else {
                    ListingItemBody.Icon(Svg.Close)
                }
            }
        }

        private val headerSpecsWithAll by lazy {
            ListingSpecs.create(
                ListingSpecs.Item(title = Res.string.device_listing__item_id, width = 50.dp) { ListingSpecs.trimmedId(it.data.id) },
                ListingSpecs.Item(title = Res.string.device_listing__item_name, width = 200.dp) { it.data.name },
                itemStatus,
                todayImpressions,
                itemLayerTop,
                itemLayerBottom,

                ListingSpecs.Item(title = Res.string.device_listing__item_company, width = 200.dp) { it.data.name_company },
                ListingSpecs.Item(title = Res.string.device_listing__item_location, width = 200.dp) { it.data.name_location },

                itemUpdateState,
            )
        }

        private val headerSpecsAtCompany by lazy {
            ListingSpecs.create(
                ListingSpecs.Item(title = Res.string.device_listing__item_id, width = 50.dp) { ListingSpecs.trimmedId(it.data.id) },
                ListingSpecs.Item(title = Res.string.device_listing__item_name, width = 200.dp) { it.data.name },
                itemStatus,
                todayImpressions,
                itemLayerTop,
                itemLayerBottom,

                ListingSpecs.Item(title = Res.string.device_listing__item_location, width = 200.dp) { it.data.name_location },

                itemUpdateState,
            )
        }

        private val headerSpecsAtLocation by lazy {
            ListingSpecs.create(
                ListingSpecs.Item(title = Res.string.device_listing__item_id, width = 50.dp) { ListingSpecs.trimmedId(it.data.id) },
                ListingSpecs.Item(title = Res.string.device_listing__item_name, width = 200.dp) { it.data.name },
                itemStatus,
                todayImpressions,
                itemLayerTop,
                itemLayerBottom,

                itemUpdateState
            )
        }


        @Composable
        fun rememberScope(): DeviceListingScope {

            val account = LocalAccountProvider.current
            val nav = LocalNavigation.current
            val args = LocalNavigationArgs.current
            val arg = args.rememberArgs(DeviceListingArgs::class)
            val vm = nav.viewModel(DeviceListingVM::class, "${arg.companyId}:${arg.locationId}:${arg.showAll}")
            val action = DeviceListingVM.Action.rememberAction(vm)

            return remember {
                object : DeviceListingScope {
                    override val vm: DeviceListingVM
                        get() = vm
                    override val action: (DeviceListingVM.Action) -> Unit
                        get() = action
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!
                    override val args: DeviceListingArgs
                        get() = arg
                    override val headerSpecs: ListingSpecs<Wrapper.Item>
                        get() = when {
                            arg.locationId != -1L -> headerSpecsAtLocation
                            !arg.showAll -> headerSpecsAtCompany
                            else -> headerSpecsWithAll
                        }


                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(listing: Io.ListingDevice) : EFieldList<Io.ListingDevice.Item, Wrapper.Item>(
        items = listing.items,
        onBindEntry = { Item(it) }
    ) {
        class Item(
            override val data: Io.ListingDevice.Item,
            override val key: String = data.id.toString(),

            ) : Entry<Io.ListingDevice.Item> {
            private var origin = data.copy()

            val name: EFieldTextString = EFieldTextString(
                property = data::name,
                condition = { it.trim().takeIf { it.length > 4 } },
                onChange = { makeChange() },
                onErr = { makeChange() })

            var change by mutableStateOf(false)
                private set

            private fun makeChange() {
                if (name.err) {
                    change = false
                } else if (origin.name != data.name) {
                    change = true
                }
            }
        }
    }
}
